import { useQuery } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { useState } from "react";
import type { Group, Role } from "@estelle/domain";

import { useDataSource } from "@/adapters/data-source";
import { Fab } from "@/components/shell/Fab";
import { LoadFailed, NoMatches } from "@/components/shell/EmptyState";
import { TableSkeleton } from "@/components/shell/LoadingState";
import { RecordDrawer } from "@/components/shell/RecordDrawer";
import { renderCell } from "@/components/table/cells";
import { DataTable } from "@/components/table/DataTable";
import { Pagination } from "@/components/table/Pagination";
import { useCursorHistory } from "@/components/table/useCursorHistory";
import { Button } from "@/components/ui/button";
import { InviteUserSheet } from "@/modules/settings/screens/InviteUserSheet";
import { UserDetail } from "@/modules/settings/screens/UserDetail";
import { projectUser } from "@/modules/settings/projections";
import type { SectionScreenProps } from "@/modules/types";
import { useOpenRecord } from "@/routes/useOpenRecord";

function nameMap(rows: readonly (Role | Group)[]): Map<string, string> {
  return new Map(rows.map((row) => [row.id, row.name]));
}

/**
 * The Users list (M8-9, FR-ACL-2, M10-7).
 *
 * "Invite user" opens `InviteUserSheet`; a row opens `UserDetail` in the
 * same `RecordDrawer` every other list uses for its selection, looked up on
 * this page the same way `ContactList` looks up the record it opens —
 * `SettingsRepository` has no `getUser(id)`, and this ERP's user list is
 * small enough that "the row is on the page that opened it" always holds.
 */
export function UsersList({
  fields,
  filter,
  sort,
  onToggleSort,
  onClearFilter,
}: SectionScreenProps) {
  const data = useDataSource();
  const { recordId, open } = useOpenRecord();
  const [inviting, setInviting] = useState(false);

  const [limit, setLimit] = useState(25);
  const [history, setHistory] = useCursorHistory();
  const cursor = history[history.length - 1];

  const usersQuery = useQuery({
    queryKey: ["settings", "users", filter, sort, limit, cursor],
    queryFn: () => data.settings.listUsers({ filter, sort, limit, cursor }),
  });

  // Roles and groups are few (four and three today) and change rarely, so
  // resolving names for a page of users is one extra pair of small requests
  // rather than a join the fixture adapter would have to grow to support.
  const rolesQuery = useQuery({
    queryKey: ["settings", "roles", "names"],
    queryFn: () => data.settings.listRoles(),
  });
  const groupsQuery = useQuery({
    queryKey: ["settings", "groups", "names"],
    queryFn: () => data.settings.listGroups(),
  });

  if (usersQuery.isPending || rolesQuery.isPending || groupsQuery.isPending) {
    return <TableSkeleton />;
  }

  if (usersQuery.isError) {
    return (
      <LoadFailed
        message={usersQuery.error.message}
        onRetry={() => {
          void usersQuery.refetch();
        }}
      />
    );
  }
  if (rolesQuery.isError) {
    return (
      <LoadFailed
        message={rolesQuery.error.message}
        onRetry={() => {
          void rolesQuery.refetch();
        }}
      />
    );
  }
  if (groupsQuery.isError) {
    return (
      <LoadFailed
        message={groupsQuery.error.message}
        onRetry={() => {
          void groupsQuery.refetch();
        }}
      />
    );
  }

  const roleNames = nameMap(rolesQuery.data.items);
  const groupNames = nameMap(groupsQuery.data.items);
  // The same simplification `TransfersList` documents for its own selection:
  // looked up against the page currently on screen, which is always where a
  // click on a row came from.
  const selectedUser = usersQuery.data.items.find(
    (user) => user.id === recordId,
  );

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="max-md:hidden flex shrink-0 justify-end border-b px-4 py-2">
        <Button
          type="button"
          size="sm"
          onClick={() => {
            setInviting(true);
          }}
        >
          <Plus />
          Invite user
        </Button>
      </div>
      <Fab
        label="Invite user"
        icon={<Plus aria-hidden />}
        onClick={() => {
          setInviting(true);
        }}
      />

      {selectedUser ? (
        <RecordDrawer
          title={selectedUser.name}
          description={selectedUser.email}
        >
          <UserDetail user={selectedUser} roles={rolesQuery.data.items} />
        </RecordDrawer>
      ) : null}

      <InviteUserSheet
        open={inviting}
        onOpenChange={setInviting}
        roles={rolesQuery.data.items}
      />

      <DataTable
        fields={fields}
        rows={usersQuery.data.items}
        getRowId={(user) => user.id}
        project={projectUser}
        sort={sort}
        onToggleSort={onToggleSort}
        onRowClick={(user) => {
          open(user.id);
        }}
        renderOverride={(field, user) => {
          if (field.name === "roleIds") {
            return renderCell(
              field,
              user.roleIds.map((id) => roleNames.get(id) ?? id),
            );
          }
          if (field.name === "groupIds") {
            return renderCell(
              field,
              user.groupIds.map((id) => groupNames.get(id) ?? id),
            );
          }
          return undefined;
        }}
        emptyState={<NoMatches onClear={onClearFilter} />}
        footer={
          <Pagination
            page={usersQuery.data}
            limit={limit}
            onLimitChange={(next) => {
              setLimit(next);
              setHistory([undefined]);
            }}
            history={history}
            onNavigate={setHistory}
          />
        }
      />
    </div>
  );
}
